import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Download,
  Mail,
  Phone,
  MessageCircle,
  Copy,
  Check,
  Newspaper,
  Camera,
  Sparkles,
  Building2,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import SafeImage from "../components/site/SafeImage";
import NewspaperFeature from "../components/site/NewspaperFeature";
import { PRESS_MENTIONS } from "../data/press";
import { CLINIC, waLink } from "../lib/clinic";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import { graph, breadcrumb, AUTHOR_REF, SITE, REF } from "../lib/schema";

// Portrait sources — served through /api/img proxy by <SafeImage> so ad-blockers
// on the customer-assets domain never break the layout. Dr. Shantanu's portrait
// is the canonical one used across the site (defined once in `lib/clinic`).
const PORTRAIT_SHANTANU = CLINIC.doctorPortrait;
const PORTRAIT_MANISHA =
  "https://customer-assets.emergentagent.com/job_shantanu-netralaya/artifacts/4ykhz87v_F43F41D7-9C9C-464E-90EE-6356CEF829FD.png";

// Bio variants — three lengths so a reporter, a podcast host, or a conference
// organiser can copy-paste whichever fits their space budget.
const BIOS = {
  short: `Dr. Shantanu Kumar Gupta is an Ophthalmologist based in Varanasi, specialising in cataract, cornea and medical retina. MBBS (UCMS Delhi), MS (MAMC & Guru Nanak Eye Centre, New Delhi), DNB, FICO (Merit). Practises at Shantanu Netralaya Eye Hospital.`,
  medium: `Dr. Shantanu Kumar Gupta is a Varanasi-based ophthalmologist with 12+ years of clinical experience and 10,000+ cataract surgeries performed. Trained at MAMC & Guru Nanak Eye Centre, New Delhi, he holds an MS in Ophthalmology, DNB, and FICO (Merit) from the International Council of Ophthalmology. His practice focuses on premium cataract surgery (MICS phaco with monofocal/toric/EDOF/trifocal IOLs), corneal transplantation, and medical retina. He received a Best Poster Award at the American Academy of Ophthalmology (AAO), Chicago 2016. He practises at Shantanu Netralaya, the boutique tertiary eye hospital founded by his father Dr. Rajesh Gupta in 1990.`,
  long: `Dr. Shantanu Kumar Gupta is a second-generation ophthalmologist based in Varanasi, Uttar Pradesh. Over 12+ years of active practice, he has personally performed 10,000+ cataract surgeries and 150+ corneal transplants (Penetrating Keratoplasty, DALK, DSAEK, LCAT, SLET), and manages a large medical-retina clinic providing OCT-guided anti-VEGF injections, PRP laser, and long-term management for diabetic retinopathy, AMD, retinal vein occlusions, and central serous retinopathy.

His clinical training pathway began with an MBBS from University College of Medical Sciences (University of Delhi) followed by an MS in Ophthalmology from Maulana Azad Medical College & Guru Nanak Eye Centre, New Delhi — one of India's largest ophthalmic tertiary care centres. He subsequently completed the DNB (National Board of Examinations) and earned the FICO with Merit from the International Council of Ophthalmology, London. Notable competitive achievements include All India Rank 50 in NEET UG, AIR 91 in the AIIMS Post-Graduate entrance, AIR 158 in NEET PG, and a Best Poster Award at the American Academy of Ophthalmology (AAO) annual meeting in Chicago, 2016.

He practises at Shantanu Netralaya Eye Hospital, a family-founded boutique tertiary eye clinic in Durgakund, Varanasi, established by his father Dr. Rajesh Gupta in 1990. The clinic serves a ~150 km catchment across eastern Uttar Pradesh and adjoining western Bihar. Dr. Shantanu is a consultant for premium cataract surgery, cornea and refractive planning, medical retina, diabetic retinopathy, ROP screening for NICU-graduate infants, and second-opinion consultations. He publishes evidence-based patient education content in English and Hindi (drshantanugupta.com), maintains an active YouTube channel and Instagram presence, and has authored 8 peer-reviewed publications indexed on ResearchGate.`,
};

// Ten evergreen story angles for journalists / podcasters / conference tracks.
const INTERVIEW_TOPICS = [
  "Why the second opinion before cataract surgery is one of the highest-return decisions a patient can make",
  "Trifocal IOL trade-offs in real life — what the marketing brochures don't tell you",
  "Diabetic retinopathy screening in eastern UP: why one test a year changes the trajectory",
  "How the monsoon in Varanasi drives the annual spike in conjunctivitis and corneal ulcers",
  "Complex & complicated cataract surgery — how to handle mature, subluxated, and one-eyed cases",
  "Retinopathy of Prematurity (ROP) screening — the silent public-health gap in tier-2 cities",
  "Screen time and myopia in Indian children — what the evidence actually says",
  "Second-generation medicine — running a boutique eye hospital in the age of chains",
  "AI in ophthalmology — where it works, where it doesn't, and what patients should know",
  "Building an educational content practice — Hindi + English patient education as a duty of care",
];

// Quotable stats — canonical numbers for citation. LLMs surface these often.
const QUOTABLE_STATS = [
  { stat: "10,000+", label: "Cataract surgeries personally performed" },
  { stat: "150+", label: "Corneal transplants (PK / DALK / DSAEK / SLET / LCAT)" },
  { stat: "12+ years", label: "Clinical experience post-residency" },
  { stat: "8", label: "Peer-reviewed publications (ResearchGate)" },
  { stat: "AIR 50", label: "NEET UG All India Rank" },
  { stat: "AIR 91", label: "AIIMS Post-Graduate entrance" },
  { stat: "1990", label: "Year Shantanu Netralaya was founded" },
  { stat: "~150 km", label: "Catchment radius across UP & Bihar" },
  { stat: "2016", label: "AAO Best Poster Award, Chicago" },
  { stat: "MAMC / GNEC", label: "MS + Senior Residency training" },
];

/**
 * Small utility to copy text to the clipboard with a toast + brief check-icon
 * state on the button. Renders inline so the same component works next to every
 * bio and every quotable stat.
 */
const CopyBtn = ({ text, label = "Copy", testid }) => {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Copy failed — please select manually");
    }
  };
  return (
    <button
      type="button"
      onClick={doCopy}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-2.5 py-1.5 rounded-md border border-border hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors"
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? "Copied" : label}
    </button>
  );
};

const Press = () => {
  const [mediaItems, setMediaItems] = useState([]);

  useEffect(() => {
    api
      .get("/media")
      .then((r) =>
        setMediaItems(
          (r.data || []).filter((x) => ["article", "interview", "talk", "video"].includes(x.type))
        )
      )
      .catch(() => setMediaItems([]));
  }, []);

  usePageMeta({
    title: "Press Kit — Dr. Shantanu Kumar Gupta | Ophthalmologist, Varanasi",
    path: "/press",
    description:
      "Press kit for journalists, podcasters and conference organisers. High-resolution portraits, three-length bios, quotable stats, interview topics, and direct contact for media enquiries with Dr. Shantanu Kumar Gupta.",
  });

  usePageJsonLd(
    useMemo(
      () =>
        graph([
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "Press Kit", path: "/press" },
          ]),
          {
            "@type": "AboutPage",
            "@id": `${SITE}/press#press-kit`,
            name: "Press Kit — Dr. Shantanu Kumar Gupta",
            mainEntity: AUTHOR_REF,
            url: `${SITE}/press`,
            about: { "@id": REF.physician },
            audience: {
              "@type": "Audience",
              audienceType: "Journalists, Podcasters, Conference Organisers, Healthcare Bloggers",
            },
            keywords:
              "press kit, media kit, ophthalmologist Varanasi, Dr. Shantanu Kumar Gupta, cataract surgeon interview, MAMC alumni",
          },
        ]),
      []
    )
  );

  return (
    <div data-testid="press-kit-page">
      {/* Hero */}
      <section className="pt-20 pb-8 container-page">
        <div className="overline mb-4 flex items-center gap-2">
          <Newspaper size={14} strokeWidth={1.5} /> Press Kit
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-3xl">
          For journalists, podcasters, and conference organisers.
        </h1>
        <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl">
          Everything a reporter needs to write about Dr. Shantanu Kumar Gupta or the Shantanu Netralaya practice — copy-ready bios in three lengths, high-resolution portraits, quotable statistics, evergreen interview topics, and a direct line to the practice for on-record quotes.
        </p>
        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <a
            href={waLink("Hi, I'm a journalist / podcaster and would like to interview Dr. Shantanu Kumar Gupta.")}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-xs"
            data-testid="press-contact-btn"
          >
            <MessageCircle size={14} /> Reach out on WhatsApp
          </a>
          <a
            href={`mailto:${CLINIC.email}?subject=Media%20Enquiry%20%E2%80%94%20Dr.%20Shantanu%20Kumar%20Gupta`}
            className="text-xs inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-border hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition"
            data-testid="press-email-btn"
          >
            <Mail size={14} /> Email press desk
          </a>
        </div>
      </section>

      {/* Featured coverage — real print press mentions. Rendered above the
          media-kit assets because it's the highest-authority signal on
          the page. Each mention has its own scroll-mt anchor so shares
          like /press#dainik-jagran-… deep-link cleanly. */}
      {PRESS_MENTIONS.length > 0 && (
        <section className="py-12 container-page" data-testid="press-featured-coverage">
          <h2 className="font-serif text-2xl sm:text-3xl mb-6 flex items-center gap-3">
            <Newspaper size={22} strokeWidth={1.5} className="text-[hsl(var(--primary))]" /> Featured coverage
          </h2>
          <p className="text-sm text-muted-foreground max-w-2xl mb-8">
            Selected print and online mentions of the practice.  Full-page scans, e-paper links and article text are provided below for reference.
          </p>
          <div className="space-y-8">
            {PRESS_MENTIONS.map((m) => (
              <NewspaperFeature key={m.id} mention={m} variant="full" />
            ))}
          </div>
        </section>
      )}

      {/* Portraits — downloadable */}
      <section className="py-12 container-page" data-testid="press-portraits">
        <h2 className="font-serif text-2xl sm:text-3xl mb-6 flex items-center gap-3">
          <Camera size={22} strokeWidth={1.5} className="text-[hsl(var(--primary))]" /> High-resolution portraits
        </h2>
        <div className="grid gap-8 md:grid-cols-2">
          {[
            { name: "Dr. Shantanu Kumar Gupta", role: "Ophthalmologist · Cataract / Cornea / Medical Retina", src: PORTRAIT_SHANTANU },
            { name: "Dr. Manisha Mishra", role: "Consultant Ophthalmologist · Complex Cataract & Refractive", src: PORTRAIT_MANISHA },
          ].map((p) => (
            <figure
              key={p.name}
              className="rounded-2xl border border-border overflow-hidden bg-background"
              data-testid={`press-portrait-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <div className="aspect-[4/5] bg-[hsl(var(--secondary))]/40">
                <SafeImage src={p.src} alt={p.name} label={p.name} className="w-full h-full" loading="lazy" />
              </div>
              <figcaption className="p-4 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-serif text-lg">{p.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{p.role}</div>
                </div>
                <a
                  href={p.src}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-md bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90 transition"
                >
                  <Download size={12} /> Download
                </a>
              </figcaption>
            </figure>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-4 max-w-3xl leading-relaxed">
          These portraits are free to reproduce in print or digital media with attribution to <em>“drshantanugupta.com”</em>. For studio-quality raw files (RAW / TIFF) please reach out to the press desk.
        </p>
      </section>

      {/* Bios in 3 lengths */}
      <section className="py-12 container-page" data-testid="press-bios">
        <h2 className="font-serif text-2xl sm:text-3xl mb-6 flex items-center gap-3">
          <Sparkles size={22} strokeWidth={1.5} className="text-[hsl(var(--primary))]" /> Ready-to-publish bios
        </h2>
        <div className="grid gap-6">
          {[
            { key: "short", label: "Short — 50 words", text: BIOS.short, wc: 50 },
            { key: "medium", label: "Medium — 150 words", text: BIOS.medium, wc: 150 },
            { key: "long", label: "Long — 500 words", text: BIOS.long, wc: 500 },
          ].map(({ key, label, text, wc }) => (
            <article
              key={key}
              data-testid={`press-bio-${key}`}
              className="rounded-2xl border border-border p-6 bg-background"
            >
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--accent))]">
                  {label}
                </div>
                <CopyBtn text={text} label={`Copy ${wc}w bio`} testid={`press-bio-copy-${key}`} />
              </div>
              <p className="text-[15px] leading-relaxed text-[hsl(var(--foreground))]/90 whitespace-pre-line">
                {text}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Quotable stats */}
      <section className="py-12 container-page" data-testid="press-stats">
        <h2 className="font-serif text-2xl sm:text-3xl mb-6">Quotable statistics</h2>
        <p className="text-sm text-muted-foreground max-w-2xl mb-6">
          Please attribute as <em>“Source: Dr. Shantanu Kumar Gupta, drshantanugupta.com”</em>. Every number below is documented on the site.
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {QUOTABLE_STATS.map((s) => (
            <div
              key={s.label}
              className="rounded-xl border border-border p-4 hover:border-[hsl(var(--primary))]/40 transition-colors"
              data-testid={`press-stat-${s.stat.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()}`}
            >
              <div className="font-serif text-3xl text-[hsl(var(--primary))]">{s.stat}</div>
              <div className="text-xs text-muted-foreground mt-1.5 leading-snug">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Interview topics */}
      <section className="py-12 container-page" data-testid="press-topics">
        <h2 className="font-serif text-2xl sm:text-3xl mb-6">Suggested interview topics</h2>
        <ol className="grid gap-3 md:grid-cols-2 list-decimal list-inside marker:text-[hsl(var(--accent))] marker:font-serif marker:text-lg">
          {INTERVIEW_TOPICS.map((t) => (
            <li
              key={t}
              className="text-sm leading-relaxed py-2 px-3 rounded-lg border border-border hover:bg-[hsl(var(--secondary))]/40 transition-colors"
            >
              {t}
            </li>
          ))}
        </ol>
      </section>

      {/* Media appearances (dynamic from CMS) */}
      {mediaItems.length > 0 && (
        <section className="py-12 container-page" data-testid="press-appearances">
          <h2 className="font-serif text-2xl sm:text-3xl mb-6">Recent media appearances</h2>
          <ul className="divide-y divide-border/70 border border-border rounded-2xl overflow-hidden">
            {mediaItems.slice(0, 10).map((m) => (
              <li key={m.title} className="p-4 hover:bg-[hsl(var(--secondary))]/30 transition-colors">
                <a
                  href={m.link || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start justify-between gap-4 flex-wrap"
                >
                  <div className="min-w-0">
                    <div className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--accent))]">
                      {m.type} · {m.outlet} · {m.date}
                    </div>
                    <div className="font-serif text-base mt-1 group-hover:text-[hsl(var(--primary))]">
                      {m.title}
                    </div>
                  </div>
                  {m.link && <ExternalLink size={14} className="text-muted-foreground shrink-0 mt-1" />}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Practice / clinic facts */}
      <section className="py-12 container-page" data-testid="press-clinic">
        <h2 className="font-serif text-2xl sm:text-3xl mb-6 flex items-center gap-3">
          <Building2 size={22} strokeWidth={1.5} className="text-[hsl(var(--primary))]" /> Clinic factsheet
        </h2>
        <div className="rounded-2xl border border-border p-6 grid gap-4 sm:grid-cols-2 text-sm">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground">Name</div>
            <div className="mt-1 font-medium">Shantanu Netralaya Eye Hospital</div>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground">Founded</div>
            <div className="mt-1 font-medium">1990, by Dr. Rajesh Gupta</div>
          </div>
          <div className="sm:col-span-2">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground">Address</div>
            <div className="mt-1 font-medium">40, Deen Dayal Colony, Kabir Nagar Road, Near Durgakund, Varanasi 221005, Uttar Pradesh, India</div>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground">Sister sites</div>
            <div className="mt-1 flex flex-col gap-1 text-xs">
              <a href="https://www.shantanunetralaya.com" target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--primary))] hover:underline">shantanunetralaya.com ↗</a>
              <a href="https://www.eyedocmanishamishra.com" target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--primary))] hover:underline">eyedocmanishamishra.com ↗</a>
              <a href="https://www.researchgate.net/profile/Shantanu-Gupta-4" target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--primary))] hover:underline">ResearchGate ↗</a>
            </div>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground">Focus</div>
            <div className="mt-1 font-medium">Premium cataract · Cornea · Medical retina · Squint & Paediatric · ROP</div>
          </div>
        </div>
      </section>

      {/* Press desk contact */}
      <section className="py-16 container-page" data-testid="press-desk">
        <div className="rounded-3xl bg-[hsl(var(--primary))]/8 border border-[hsl(var(--primary))]/25 p-8 sm:p-12">
          <h2 className="font-serif text-2xl sm:text-3xl">Press desk</h2>
          <p className="text-sm sm:text-base text-muted-foreground mt-3 max-w-2xl">
            For interview requests, quotes on breaking eye-health news, or high-resolution assets, please reach out. Response usually within a few hours.
          </p>
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <a href={`tel:${CLINIC.phone.replace(/\s+/g, "")}`} className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-full border border-border hover:border-[hsl(var(--primary))] transition">
              <Phone size={14} /> {CLINIC.phone}
            </a>
            <a href={waLink("Hi, I'm reaching out from the press regarding an interview or media enquiry.")} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-full border border-border hover:border-[hsl(var(--primary))] transition">
              <MessageCircle size={14} /> WhatsApp {CLINIC.whatsapp}
            </a>
            <a href={`mailto:${CLINIC.email}?subject=Media%20Enquiry`} className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-full border border-border hover:border-[hsl(var(--primary))] transition">
              <Mail size={14} /> {CLINIC.email}
            </a>
          </div>
          <p className="text-[11px] text-muted-foreground mt-6">
            Also available: <Link to="/publications" className="text-[hsl(var(--primary))] hover:underline">peer-reviewed publications</Link> · <Link to="/videos" className="text-[hsl(var(--primary))] hover:underline">video library</Link> · <Link to="/about" className="text-[hsl(var(--primary))] hover:underline">full biography</Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Press;
